import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import omit from 'lodash/omit';

import { ConfigService } from '../../config.service';
import { LoggingService } from '../../logging.service';
import { Container, ContainerInfo } from '../../shared/models/container.model';
import { Profile } from '../../shared/models/profile.model';
import { hash } from '../../shared/string.utils';
import { createDicomStudy, createImageStudy, createNiftiStudy, Study } from './legacy-ohif.utils';

interface RequestConfig {
  container: string
  fileInfo: string
  params: {
    containerId: string
    fileName: string
  }
}

interface LegacyOhifViewerData {
  studies: Study[],
  requestConfig: RequestConfig,
  info: ContainerInfo,
  profile: Profile,
  parent: Container,
  sliceOrder?: {
    current: string,
    desired: boolean | string,
  }
}

export interface LegacyOhifViewerDialogData {
  requestConfig?: RequestConfig
  format?: string
  images?: string[]
  info?: ContainerInfo
  parent?: Container
  profile?: Profile
  title?: string
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'legacy-ohif-viewer-dialog',
  },
  selector: 'legacy-ohif-viewer-dialog',
  styleUrls: ['legacy-ohif-viewer-dialog.component.scss'],
  templateUrl: './legacy-ohif-viewer-dialog.component.html',
})
export class LegacyOhifViewerDialog implements OnInit {
  public images: string[]
  public title: string
  public iframeSrc: SafeResourceUrl
  public isLoading = true

  constructor(
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) private data: LegacyOhifViewerDialogData = {},
    public dialogRef: MatDialogRef<LegacyOhifViewerDialog>,
    private logger: LoggingService,
    private ref: MatDialogRef<LegacyOhifViewerDialog>,
    private sanitizer: DomSanitizer,
  ) {
    this.title = data.title;
    // removes the "ticket=xxx" from the acquisition URLs as the viewer
    // uses the Authentication header with the "satellizer_token" to make file requests
    const images = data.images.map((url) => url.replace(/ticket=.+&|ticket=.+$/, ''));
    const ohifData = this.createOhifData(images);
    this.setupIframe(ohifData);
    // TODO
    // register events that allow communication between the iframe and the application
    // setTimeout(() => registerOHIFEvents());
  }

  ngOnInit(): void {
    this.ref.addPanelClass('padding-0');
  }

  async setupIframe(ohifData: LegacyOhifViewerData): Promise<void> {
    // generate hash from serialized data to be used in local storage key
    const serializedData = JSON.stringify(ohifData);
    const hashValue = await hash(serializedData);
    const localStorageKey = `flywheelOHIFViewerData-${hashValue}`;
    window.localStorage.setItem(localStorageKey, serializedData);
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(`src/common/ohifViewer/?key=${localStorageKey}`);
  }

  createOhifData(images: string[]): LegacyOhifViewerData {
    let study: Study;
    switch (this.data.format) {
      case 'dicom':
        study = createDicomStudy(images);
        break;
      case 'nifti':
        study = createNiftiStudy(images[0]);
        break;
      case 'image':
        study = createImageStudy(images);
        break;
      default:
        this.logger.error(`The format ${this.data.format} is not supported by the OHIF viewer.`);
        // but pretend it's a dicom file anyway to let the viewer detect it's not a valid file
        study = createDicomStudy(images);
    }
    // break circular references
    let parentContainer;
    if (this.data.parent && this.data.parent.files) {
      parentContainer = {
        ...this.data.parent,
        files: this.data.parent.files.map((file) => omit(file, 'parent')),
      };
    }
    // assembles a data object in the format expected by the viewer
    const ohifData: LegacyOhifViewerData = {
      studies: [study],
      requestConfig: this.data.requestConfig,
      info: this.data.info,
      profile: this.data.profile,
      parent: parentContainer,
    };
    if (this.data.format === 'dicom') {
      ohifData.sliceOrder = {
        current: 'alpha',
        desired: this.configService.getFeatureSetting('ohif_slice_order'),
      };
    }
    return ohifData;
  }

  onLoadIframe(): void {
    setTimeout(() => {
      this.isLoading = false;
    });
  }
}
